import React from 'react';
import PropTypes from 'prop-types';
import { Container } from 'semantic-ui-react';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import SocialLinks, {
  hasSocialLinks as hasSocialLinksOf,
} from '../Helpers/SocialLinks';
import DefaultViewWithoutTitle from '../../Custom/DefaultViewWithoutTitle';
import { vocabTitle } from '../../../helpers/content';
import Image from '@plone/volto/components/theme/Image/Image';
import { flattenRichTextToAppURL } from '../../../helpers/richText';

const PeopleView = (props) => {
  const { content } = props;

  const degreeLabel = vocabTitle(content.type_of_degree) || null;
  const positionLabel =
    content.type_of_position?.title ||
    content.type_of_position?.token ||
    content.type_of_position ||
    null;

  // A full object's scale download is already the complete image URL; it
  // only needs flattening, as it carries the internal backend URL during SSR.
  const imageUrl = content.image?.scales?.large?.download
    ? flattenToAppURL(content.image.scales.large.download)
    : '/people_default_image.svg';

  const hasContact =
    content.office ||
    content.telephone ||
    content.address ||
    content.email ||
    content.fax;

  const hasResearchAreas =
    content.research_areas?.data && content.research_areas.data !== '<p></p>';

  const hasSocialLinks = hasSocialLinksOf(content);

  const hasSecondBlock = hasResearchAreas || hasSocialLinks;

  const hasAbout = content.about?.data && content.about.data !== '<p></p>';

  return (
    <Container id="view-wrapper" className="PeopleView">
      {/* Block 1: Portrait + Name + Contact */}
      <div className="view-people-layout">
        {/* Portrait — always reserved, fixed size */}
        <div className="view-people-layout__image">
          <Image
            src={imageUrl}
            alt={content.image_caption || content.full_name}
          />
        </div>

        {/* Right column: Name + Contact */}
        <div className="view-people-layout__content">
          <div className="view-people-layout__name">
            {degreeLabel && (
              <p className="view-header__subtitle">{degreeLabel}</p>
            )}
            <h2 className="view-header__title">
              {content.full_name || content.id}
            </h2>
            {positionLabel && (
              <p className="view-header__subtitle">{positionLabel}</p>
            )}
          </div>

          {hasContact && (
            <div className="view-section">
              <h3 className="view-section__label">Contact:</h3>
              <div className="view-section__contact">
                {content.office && (
                  <>
                    <span className="view-section__contact-label">Room:</span>
                    <span className="view-section__contact-value">
                      {content.office}
                    </span>
                  </>
                )}
                {content.telephone && (
                  <>
                    <span className="view-section__contact-label">Tel.:</span>
                    <span className="view-section__contact-value">
                      {content.telephone}
                    </span>
                  </>
                )}
                {content.email && (
                  <>
                    <span className="view-section__contact-label">Email:</span>
                    <span className="view-section__contact-value">
                      <a href={`mailto:${content.email}`}>{content.email}</a>
                    </span>
                  </>
                )}
                {content.fax && (
                  <>
                    <span className="view-section__contact-label">Fax:</span>
                    <span className="view-section__contact-value">
                      {content.fax}
                    </span>
                  </>
                )}
                {content.address && (
                  <>
                    <span className="view-section__contact-label">
                      Address:
                    </span>
                    <span className="view-section__contact-value view-section__contact-value--lines">
                      {content.address}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Block 2: Research Interest and Social Links, two columns */}
      {hasSecondBlock && (
        <>
          <div className="view-divider" />
          <div className="view-people-second-grid">
            {/* Left: Research Interest */}
            <div>
              {hasResearchAreas && (
                <div className="view-section">
                  <h3 className="view-section__label">Research Interest:</h3>
                  <div
                    className="view-section__richtext"
                    dangerouslySetInnerHTML={{
                      __html: flattenRichTextToAppURL(
                        content.research_areas.data,
                      ),
                    }}
                  />
                </div>
              )}
            </div>

            {/* Right: Social Links */}
            <div>
              {hasSocialLinks && (
                <div className="view-section">
                  <h3 className="view-section__label">Links:</h3>
                  <SocialLinks content={content} />
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* About — full width */}
      {hasAbout && (
        <>
          <div className="view-divider" />
          <div className="view-section">
            <h3 className="view-section__label">About:</h3>
            <div
              className="view-section__richtext"
              dangerouslySetInnerHTML={{
                __html: flattenRichTextToAppURL(content.about.data),
              }}
            />
          </div>
        </>
      )}

      {/* The block-rendered part of the page */}
      <DefaultViewWithoutTitle {...props} />
    </Container>
  );
};

PeopleView.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.string,
    full_name: PropTypes.string,
    type_of_degree: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ title: PropTypes.string }),
    ]),
    type_of_position: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ title: PropTypes.string, token: PropTypes.string }),
    ]),
    image: PropTypes.shape({ scales: PropTypes.object }),
    image_caption: PropTypes.string,
    office: PropTypes.string,
    telephone: PropTypes.string,
    address: PropTypes.string,
    email: PropTypes.string,
    fax: PropTypes.string,
    research_areas: PropTypes.shape({ data: PropTypes.string }),
    website: PropTypes.string,
    twitter: PropTypes.string,
    mastodon: PropTypes.string,
    bluesky: PropTypes.string,
    linkedin: PropTypes.string,
    github: PropTypes.string,
    google_scholar: PropTypes.string,
    orcid_id: PropTypes.string,
    researchgate: PropTypes.string,
    about: PropTypes.shape({ data: PropTypes.string }),
  }).isRequired,
};

export default PeopleView;
